import * as React from 'react'
import { CSSProperties, FunctionComponent, PropsWithChildren, ReactElement, useContext, useEffect, useMemo, useRef, useState } from 'react'
import ErrorBoundary from '../components/Diagnostics/ErrorBoundary'
import Router from './Router'
import { FileProvider } from '../context/FileContext'
import { MessageProvider } from '../context/MessageContext'
import MainMenu from '../components/MainMenu'
import Errors from '../components/Diagnostics/Errors'
import Commands from './Commands'
import QueueToaster from '../components/Toast/QueueToaster'
import AppToasterOutlet, { AppToastProvider } from '../components/Toast/AppToaster'
import { MemoryRouter } from 'react-router-dom'
import axios from '../helpers/axiosHelper'
import AxiosContext from '../context/AxiosContext'
import { Route } from 'react-router'
import {
    FluentProvider,
    makeStyles,
    mergeClasses,
    MessageBar,
    MessageBarBody,
    shorthands,
    Spinner,
    tokens,
    webDarkTheme,
    webLightTheme,
} from '@fluentui/react-components'
import Version from '../v2/components/Footer/Version'
import Support from '../v2/components/Diagnostics/Support'
import FileLink from '../v2/components/Footer/FileLink'
import { GenerationQueueProvider } from '../context/QueueContext'
import { MollieProvider } from '../context/MollieContext'
import { useTranslation } from 'react-i18next'
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js'
import { appInsights, appInsightsReact } from '../helpers/appInsights'
import { I18NProvider } from '../context/I18NContext'
import MailboxAvatar from '../components/Diagnostics/MailboxAvatar'
import AuthorizationContext, { AuthorizationProvider } from '../context/AuthorizationContext'
import { GlobalProvider } from '../context/GlobalContext'
import { AzureWebSocketProvider } from '../context/AzureWebSocketContext'
import { OfficeEventHandlerProvider } from '../context/OfficeEventHandlerContext'
import useUser from '../hooks/useUser'
import SelectHost from '../components/Authentication/SelectHost'
import BootstrapThrottled from '../components/Authentication/BootstrapThrottled'
import useTokenInjector from '../hooks/identity/useTokenInjector'
import useTokenErrorHandler from '../hooks/identity/useTokenErrorHandler'
import { AxiosError, AxiosResponse } from 'axios'
import { useMount } from '@fluentui/react-hooks'
import useAxios from '../hooks/useAxios'
import TimeSuggestionCounter from '../v2/components/Footer/TimeSuggestionCounter'
import Loading from '../components/Loading'
import FirstVisit from './Intro/FirstVisit'
import useLocalStorage from '../hooks/useLocalStorage'
import useResolvedTheme from '../hooks/useResolvedTheme'
import { PopupProvider } from '../context/PopupContext'
import useViewport, { Viewport } from '../hooks/useViewport'
import { createGraphRetryHandler } from '../helpers/graphRetry'
import ConnectivityContext, { ConnectivityProvider } from '../context/ConnectivityContext'
import { SharePointProvider } from '../context/SharePointContext'
import { ExchangeIdProvider, useExchangeIdStore } from '../context/ExchangeIdContext'

export { appInsights, appInsightsReact } from '../helpers/appInsights'

export const useApplicationStyles = makeStyles({
    wrapper: {
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: '#eff6fc',
        backgroundImage: "url('../images/hammock-bg.png')",
        backgroundSize: '1200px 600px',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        minHeight: '100%',
    },
    // Applied on top of `wrapper` when the resolved theme is dark. Keyed off useResolvedTheme
    // instead of `@media (prefers-color-scheme: dark)` so the manual toggle works too.
    wrapperDark: {
        backgroundColor: '#000000',
        backgroundImage: 'unset',
    },
    container: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minWidth: 0,
        maxWidth: '1280px',
        backgroundColor: tokens.colorNeutralBackgroundDisabled,
        boxShadow: tokens.shadow16,

        '> :not(:first-child)': {
            marginTop: tokens.spacingVerticalS,
        },
    },
    containerMax: {
        maxWidth: '100%',
    },
    accordionContent: {
        display: 'grid',
        ...shorthands.gap(tokens.spacingVerticalM),
    },
    sticky: {
        position: 'sticky',
        zIndex: 10000,
        top: 'var(--sticky-top)',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,

        marginLeft: tokens.spacingHorizontalS,
        marginRight: tokens.spacingHorizontalS,

        '@media screen and (max-width: 400px)': {
            marginLeft: tokens.spacingHorizontalXS,
            marginRight: tokens.spacingHorizontalXS,
        },
    },
    subMenu: {
        position: 'sticky',
        top: '44px',
        zIndex: 7,
        backgroundColor: tokens.colorNeutralBackground2,
    },
    actionItems: {
        position: 'sticky',
        top: '88px',
        zIndex: 8,
        backgroundColor: tokens.colorNeutralBackground2,
    },
    footer: {
        display: 'flex',
        // Never wrap: the left section (file links) collapses into its overflow menu instead, so Support,
        // MailboxAvatar and Version stay on the footer's single row.
        flexWrap: 'nowrap',
        justifyContent: 'space-between',
        position: 'sticky',
        bottom: 0,
        zIndex: 1000,
        height: '40px',
        backgroundColor: tokens.colorBrandBackground2,
    },
    footerSection: {
        display: 'flex',
        gap: tokens.spacingVerticalS,
    },
    // The section that gives way when the footer runs out of room. It has to grow as well as shrink: FileLink's
    // Overflow measures its container, and a container sized by its own content measures 0 on the first pass and
    // then stays collapsed. Growing into the space the fixed section leaves over makes the width external.
    footerSectionShrink: {
        flexGrow: 1,
        flexShrink: 1,
        minWidth: 0,
    },
    // The section that must stay intact and fully visible.
    footerSectionFixed: {
        flexShrink: 0,
    },
    minHeight: {
        minHeight: 'fit-content',
    },

    thinTextButton: {
        fontWeight: tokens.fontWeightRegular,
    },
    displayFlex: {
        display: 'flex',
    },
    flexWrap: {
        flexWrap: 'wrap',
    },
    flexAlignItemsStart: {
        alignItems: 'start',
    },
    flexColumn: {
        display: 'flex',
        flexDirection: 'column',
    },
    flexRow: {
        display: 'flex',
        flexDirection: 'row',
    },
    flexGrow: {
        flexGrow: 1,
    },
    gapS: {
        gap: tokens.spacingVerticalS,
    },
    selfCenter: {
        alignSelf: 'center',
    },
    selfStart: {
        alignSelf: 'start',
        justifyContent: 'start',
    },
    verticalAlign: {
        verticalAlign: 'middle',
    },
    alignItems: {
        alignItems: 'center',
    },
    alignSelf: {
        alignSelf: 'center',
    },
    spaceBetween: {
        display: 'flex',
        gap: tokens.spacingVerticalS,
        justifyContent: 'space-between',
    },
    textNoWrapOverflowHidden: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
    },
    overflowXHidden: {
        overflowX: 'hidden',
        maxWidth: 'inherit',
    },
    width100: {
        width: '100%',
    },
    height100: {
        height: '100%',
    },
})

const Template: FunctionComponent<PropsWithChildren<{ authenticated: boolean }>> = props => {
    const styles = useApplicationStyles()
    const { showOnlineInfo, showOfflineWarning } = useContext(ConnectivityContext)
    const { t: translate } = useTranslation()
    const { viewport, isViewport } = useViewport()
    const { resolvedTheme } = useResolvedTheme()

    const smallMenu = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const { loaded: loadedMaxWidth, values: maxWidth } = useLocalStorage<boolean>({
        key: 'maxWidth',
        defaultValue: false,
        fromJson: values => values,
        toJson: values => values,
    })

    return (
        <div
            id={'wrapper'}
            aria-label={'wrapper'}
            className={mergeClasses(styles.wrapper, resolvedTheme === 'dark' && styles.wrapperDark)}
            style={
                {
                    '--sticky-top': '0px',
                } as CSSProperties
            }
        >
            <div
                id={'contentcontainer'}
                aria-label={'contentcontainer'}
                className={mergeClasses(styles.container, loadedMaxWidth && maxWidth && styles.containerMax)}
            >
                {props.authenticated && (
                    <div aria-label={'header'} className={mergeClasses(styles.flexColumn, styles.sticky)}>
                        <MainMenu />
                        {showOfflineWarning && (
                            <MessageBar intent={'warning'}>
                                <MessageBarBody>{translate('base.overview.internet.offline')}</MessageBarBody>
                            </MessageBar>
                        )}
                        {showOnlineInfo && (
                            <MessageBar intent={'success'}>
                                <MessageBarBody>{translate('base.overview.internet.online')}</MessageBarBody>
                            </MessageBar>
                        )}
                    </div>
                )}
                <Errors />
                <div
                    aria-label={'content'}
                    className={styles.content}
                    style={
                        {
                            '--sticky-top': smallMenu ? '64px' : '76px',
                        } as CSSProperties
                    }
                >
                    {props.children}
                    {props.authenticated && <Commands />}
                    {/* Renders a toast for every job on the queue. Only mounted when authenticated, because the
                        unauthenticated Template below sits outside the GenerationQueueProvider. */}
                    {props.authenticated && <QueueToaster />}
                    <AppToasterOutlet />
                </div>
                <div aria-label={'footer'} className={styles.footer}>
                    <div className={mergeClasses(styles.footerSection, styles.footerSectionShrink)}>
                        {props.authenticated && <TimeSuggestionCounter />}
                        <FileLink />
                    </div>
                    <div className={mergeClasses(styles.footerSection, styles.footerSectionFixed)}>
                        <Support />
                        {props.authenticated && <MailboxAvatar />}
                        <Version />
                    </div>
                </div>
            </div>
        </div>
    )
}

const InsightsContainer: FunctionComponent<PropsWithChildren> = props => {
    return <AppInsightsContext.Provider value={appInsightsReact}>{props.children}</AppInsightsContext.Provider>
}

/**
 * Stamps every piece of telemetry with the user and host that caused it, see MONITORING.md. Both values always get
 * a sentinel: setAuthenticatedUserContext validates its first argument and stores nothing at all when it is empty,
 * the host included, so there is no way to stamp the host on its own.
 *
 * There is exactly one of these mounted at a time. Two of them would fight over the same two values: React runs a
 * child's effect before its parent's, so a parent that only knows the sentinel would overwrite the user the child
 * had just stamped on every host change.
 */
const InsightsIdentity: FunctionComponent<{ userId?: string }> = props => {
    const { host } = useContext(AuthorizationContext)

    useEffect(() => {
        appInsights.setAuthenticatedUserContext(props.userId || 'anonymous', host?.RowKey || 'global', false)

        return () => appInsights.clearAuthenticatedUserContext()
    }, [host?.RowKey, props.userId])

    return null
}

/**
 * The authenticated half of the stamping. It has to sit inside GlobalProvider: the HAMMOCK user id comes from the
 * users list that provider loads, so anywhere above it `currentUser` stays undefined and every event is stamped
 * `anonymous`. That was the case until this change; it went unnoticed because setAuthenticatedUserContext used to
 * run with storeInCookie, and the ai_authUser cookie kept filling the identity back in on each page load. Dropping
 * that cookie (see appInsights.ts) laid the broken wiring bare - from 24-08-2026 not a single event carried a user.
 */
const AuthenticatedInsightsIdentity: FunctionComponent = () => {
    const { currentUser } = useUser()

    return <InsightsIdentity userId={currentUser?.id} />
}

const AuthenticatedContainer: FunctionComponent<PropsWithChildren> = () => {
    return (
        <MemoryRouter initialEntries={[window.location.hash.substring(1)]}>
            <GlobalProvider>
                <AuthenticatedInsightsIdentity />
                <GenerationQueueProvider>
                    <FileProvider>
                        <MessageProvider>
                            <MollieProvider>
                                <PopupProvider>
                                    <AzureWebSocketProvider>
                                        <Template authenticated={true}>
                                            <Route path="/" component={Router} />
                                        </Template>
                                    </AzureWebSocketProvider>
                                </PopupProvider>
                            </MollieProvider>
                        </MessageProvider>
                    </FileProvider>
                </GenerationQueueProvider>
            </GlobalProvider>
        </MemoryRouter>
    )
}

const Container: FunctionComponent<PropsWithChildren> = () => {
    const axios = useAxios()
    const { t: translate } = useTranslation()
    const tokenInjector = useTokenInjector()
    const tokenErrorHandler = useTokenErrorHandler()

    const { identified, authenticated, host: currentHost, mailbox, bootstrapBlocked } = useContext(AuthorizationContext)
    const exchangeIds = useExchangeIdStore()

    // The interceptors are registered once, but the mailbox they have to attribute a 429 to changes with the
    // message the user opens, so it is read through a ref rather than captured.
    const mailboxRef = useRef(mailbox)
    mailboxRef.current = mailbox

    useMount(() => {
        axios.interceptors.request.use(tokenInjector, error => Promise.reject(error))
        const graphRetryHandler = createGraphRetryHandler(axios, ({ url, until }) => {
            // translateExchangeIds is deliberately not retried: skipping it costs the user nothing, because the
            // caller then addresses the message by its mutable rest id, exactly as it already does for the
            // tenants where Graph answers 403.
            if (url?.includes('translateExchangeIds')) {
                exchangeIds.throttleUntil(mailboxRef.current, until)
            }
        })
        axios.interceptors.response.use((value: AxiosResponse) => value, graphRetryHandler)
        axios.interceptors.response.use(
            (value: AxiosResponse) => value,
            (error: AxiosError) => new Promise<AxiosResponse>(tokenErrorHandler(error)),
        )
    })

    useEffect(() => {
        // Route to the correct base path so that each user gets the correct version served.
        if (currentHost?.ClientPath && window.location.pathname !== currentHost?.ClientPath) {
            // Redirect if we're not in the correct environment.
            // window.location.href = host.clientPath + window.location.search + window.location.hash
            const newLocation = currentHost?.ClientPath + window.location.search + (window.location.hash || '#') + window.location.pathname
            window.location.replace(newLocation)
        }
    }, [currentHost])

    const {
        loaded: loadedFirstVisit,
        values: firstVisit,
        setValues: setFirstVisit,
    } = useLocalStorage<boolean, boolean>({
        key: 'firstVisit',
        defaultValue: true,
        fromJson: values => values,
        toJson: values => values,
    })

    //Suppress the first visit carousel when we're in a production host.
    useEffect(() => {
        if (!['dev', 'test'].includes(process.env.BOOTSTRAP_ENV)) {
            setFirstVisit(false)
        }
    }, [firstVisit])

    if (!authenticated) {
        return (
            <Template authenticated={false}>
                <InsightsIdentity />
                {currentHost?.ClientPath && window.location.pathname !== currentHost?.ClientPath && (
                    <Spinner label={translate('authentication.host.redirect')} />
                )}
                {!loadedFirstVisit && <Loading />}
                {loadedFirstVisit && firstVisit && (
                    <FirstVisit
                        setVisited={() => {
                            setFirstVisit(false)
                        }}
                    />
                )}
                {/* Office is rate limiting our token requests and we have stopped retrying on our
                    own. Takes precedence over every spinner below: none of them would ever resolve. */}
                {loadedFirstVisit && !firstVisit && bootstrapBlocked && <BootstrapThrottled />}
                {loadedFirstVisit && !firstVisit && !bootstrapBlocked && !identified && <Loading />}
                {loadedFirstVisit && !firstVisit && !bootstrapBlocked && identified && !currentHost && <SelectHost />}
                {loadedFirstVisit && !firstVisit && !bootstrapBlocked && identified && currentHost && !authenticated && <Loading />}
            </Template>
        )
    }

    return <AuthenticatedContainer />
}

export default function App(): ReactElement {
    const [currentVersion] = useState(process.env.COMMITHASH)
    const [currentVersionTimeout, setCurrentVersionTimeout] = useState<NodeJS.Timeout>(undefined)

    const { resolvedTheme } = useResolvedTheme()

    const fetchNewVersion = () => {
        const commitHashUrl = window.location.origin + (window.location.pathname + '/COMMITHASH').replaceAll('//COMMITHASH', '/COMMITHASH')

        fetch(commitHashUrl, { cache: 'no-cache', headers: { 'Cache-Control': 'no-cache' } })
            .then(response => {
                if (response.ok) {
                    return response.text()
                } else {
                    console.log('Could not load /COMMITHASH: status: ' + response.status)
                    return currentVersion
                }
            })
            .then(response => {
                // COMMITHASH contains a git SHA (not a UUID); anything else means we fetched garbage.
                const version = response?.trim()
                return /^[0-9a-f]{40}$/i.test(version) ? version : currentVersion
            })
            .then((newVersion: string) => {
                console.log(`Update check currentVersion=${currentVersion} , newVersion=${newVersion}`)
                if (currentVersion !== newVersion) {
                    window.location.reload()
                } else {
                    setCurrentVersionTimeout(setTimeout(() => fetchNewVersion(), 3600000))
                }
            })
            .catch(error => {
                if (error?.message?.includes('ERR_NETWORK') || error?.name?.includes('ERR_NETWORK')) {
                    setCurrentVersionTimeout(setTimeout(() => fetchNewVersion(), 300000))
                }
            })
    }

    useEffect(() => {
        //Skip this for DEV as this uses webpack.
        if (!['dev'].includes(process.env.BOOTSTRAP_ENV)) {
            fetchNewVersion()
        }

        return () => currentVersionTimeout && clearTimeout(currentVersionTimeout)
    }, [])

    return (
        <FluentProvider theme={resolvedTheme === 'dark' ? webDarkTheme : webLightTheme}>
            <OfficeEventHandlerProvider>
                <AppToastProvider>
                    <ConnectivityProvider>
                        <I18NProvider>
                            <ErrorBoundary>
                                <AxiosContext.Provider value={axios}>
                                    <AuthorizationProvider>
                                        <SharePointProvider>
                                            <ExchangeIdProvider>
                                                <InsightsContainer>
                                                    <Container />
                                                </InsightsContainer>
                                            </ExchangeIdProvider>
                                        </SharePointProvider>
                                    </AuthorizationProvider>
                                </AxiosContext.Provider>
                            </ErrorBoundary>
                        </I18NProvider>
                    </ConnectivityProvider>
                </AppToastProvider>
            </OfficeEventHandlerProvider>
        </FluentProvider>
    )
}
